/**
 * Emergent Marketplace — Phase 4
 * VendorBookings.jsx
 * Architectural Layer: Presentation → Vendor Dashboard
 *
 * Vendor view of all incoming bookings:
 *   - Pending requests (need confirm/decline)
 *   - Upcoming confirmed sessions
 *   - Past completed sessions
 *   - Guest bookings (Mode 2) vs in-app (Mode 1)
 *
 * Design: Focused and calm — warm sand, deep teal, clear status signaling.
 * Font: Literata (display) + Source Sans 3 (UI)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Calendar, Clock, Check, X, User, Mail, Phone,
  ChevronDown, ChevronUp, Video, MessageSquare,
  AlertCircle, Star, Globe, Loader, CheckCircle, CalendarClock, ClipboardList, Printer
} from "lucide-react";
import { apiFetch } from "./api";
import { Pagination } from "../../components/Pagination";
import PlaceSessionPanel from "../../components/marketplace/PlaceSessionPanel";
import BookingsCalendar from "../../components/marketplace/BookingsCalendar";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Literata:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Source+Sans+3:wght@300;400;600&display=swap');

  .vb-root {
    --sand:        #faf7f2;
    --sand-dark:   #f0ebe0;
    --teal:        #1a5a44;
    --teal-mid:    #2a7a5e;
    --teal-bg:     #eaf5f0;
    --teal-border: #a0d8c4;
    --white:       #ffffff;
    --border:      #e4ddd4;
    --ink:         #1a1a16;
    --ink-mid:     #5a5a54;
    --ink-light:   #9a9a90;
    --amber:       #9a6010;
    --amber-bg:    #fef9ea;
    --amber-border:#e8d080;
    --blue:        #1a50a0;
    --blue-bg:     #eef4fd;
    --blue-border: #b0cce8;
    --red:         #a02828;
    --red-bg:      #fdf0f0;
    --red-border:  #e8b0b0;
    --purple:      #6030a0;
    --purple-bg:   #f4eef8;
    font-family: 'Source Sans 3', sans-serif;
    background: var(--sand);
    color: var(--ink);
    min-height: 100vh;
  }

  .vb-header {
    background: var(--white);
    border-bottom: 1px solid var(--border);
    padding: 18px 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 20;
  }

  .vb-header h1 {
    font-family: 'Literata', serif;
    font-size: 21px;
    font-weight: 600;
    color: var(--teal);
    margin: 0 0 2px;
  }

  .vb-header p { font-size: 13px; color: var(--ink-light); margin: 0; }

  /* Tabs */
  .vb-tabs {
    display: flex;
    padding: 0 28px;
    background: var(--white);
    border-bottom: 1px solid var(--border);
    overflow-x: auto;
  }

  .vb-tab {
    padding: 11px 20px;
    border: none; background: none;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px; font-weight: 600;
    color: var(--ink-light);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.15s;
    white-space: nowrap;
    display: flex; align-items: center; gap: 7px;
  }

  .vb-tab.active { color: var(--teal); border-bottom-color: var(--teal); }

  .vb-tab-badge {
    background: var(--amber-bg);
    color: var(--amber);
    border: 1px solid var(--amber-border);
    border-radius: 10px;
    padding: 1px 7px;
    font-size: 10px;
    font-weight: 700;
  }

  .vb-tab.active .vb-tab-badge {
    background: var(--teal);
    color: white;
    border-color: transparent;
  }

  /* Buttons that were asked for by name but never defined, so they came out as bare
     browser grey next to everything else (found while merging the calendar in, Jun 2026). */
  .vb-btn {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px; font-weight: 600;
    padding: 8px 14px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--white);
    color: var(--ink-mid);
    cursor: pointer;
    display: inline-flex; align-items: center; gap: 6px;
    transition: border-color 0.15s, color 0.15s, background 0.15s, opacity 0.15s;
  }
  .vb-btn:hover { border-color: var(--teal); color: var(--ink); }
  .vb-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .vb-btn-primary {
    background: var(--teal); border-color: var(--teal); color: #fff;
  }
  .vb-btn-primary:hover { background: var(--teal-mid); border-color: var(--teal-mid); color: #fff; }

  /* The board above the ribbon: month view, scheduling form, notices */
  .vb-board { padding: 16px 28px 0; }
  @media (max-width: 760px) { .vb-board { padding: 12px 14px 0; } }

  /* List */
  .vb-list { padding: 16px 28px 40px; display: flex; flex-direction: column; gap: 10px; }

  /* Booking card */
  .vb-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 14px;
    overflow: hidden;
    transition: box-shadow 0.15s;
  }

  .vb-card:hover { box-shadow: 0 3px 14px rgba(0,0,0,0.06); }
  .vb-card-focused {
    border-color: var(--teal);
    box-shadow: 0 0 0 3px rgba(26,90,68,0.14);
  }

  .vb-card-head {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    cursor: pointer;
    user-select: none;
  }

  .vb-card-avatar {
    width: 42px; height: 42px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--teal), var(--teal-mid));
    color: white;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Literata', serif;
    font-size: 17px; font-weight: 600;
    flex-shrink: 0;
  }

  .vb-card-avatar.guest {
    background: linear-gradient(135deg, #3040a0, #5060c0);
  }

  .vb-card-info { flex: 1; min-width: 0; }

  .vb-card-name {
    font-family: 'Literata', serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 3px;
  }

  .vb-card-meta {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
    font-size: 12px;
    color: var(--ink-light);
  }

  .vb-card-meta-item { display: flex; align-items: center; gap: 4px; }

  /* Status badges */
  .vb-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  .vb-status-requested  { background: var(--amber-bg); color: var(--amber); border: 1px solid var(--amber-border); }
  .vb-status-confirmed  { background: var(--teal-bg);  color: var(--teal);  border: 1px solid var(--teal-border); }
  .vb-status-completed  { background: var(--sand-dark); color: var(--ink-mid); border: 1px solid var(--border); }
  .vb-status-cancelled  { background: var(--red-bg);   color: var(--red);   border: 1px solid var(--red-border); }

  .vb-source-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  .vb-source-in_app { background: var(--teal-bg); color: var(--teal); }
  .vb-source-widget { background: var(--purple-bg); color: var(--purple); }
  .vb-source-guest  { background: var(--blue-bg); color: var(--blue); }

  .vb-session-chip {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: var(--ink-mid);
    background: var(--sand);
    padding: 3px 9px;
    border-radius: 8px;
    border: 1px solid var(--border);
    font-weight: 600;
  }

  /* Card expand */
  .vb-expand-btn {
    background: none; border: none; cursor: pointer;
    color: var(--ink-light); padding: 4px; display: flex;
  }

  .vb-card-detail {
    border-top: 1px solid var(--sand-dark);
    background: #fafaf8;
    padding: 18px;
    animation: vb-expand 0.18s ease;
  }

  @keyframes vb-expand {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .vb-detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  .vb-detail-field label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ink-light);
    margin-bottom: 4px;
  }

  .vb-detail-field p {
    font-size: 14px;
    color: var(--ink);
    margin: 0;
    line-height: 1.45;
  }

  /* Confirm message input */
  .vb-confirm-msg {
    width: 100%;
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 9px;
    padding: 10px 12px;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px;
    color: var(--ink);
    outline: none;
    resize: vertical;
    min-height: 60px;
    box-sizing: border-box;
    margin-bottom: 12px;
    transition: border-color 0.15s;
  }

  .vb-confirm-msg:focus { border-color: var(--teal); background: var(--white); }

  /* Action buttons */
  .vb-actions { display: flex; gap: 10px; flex-wrap: wrap; }

  .vb-btn-confirm {
    display: flex; align-items: center; gap: 7px;
    padding: 9px 20px;
    border-radius: 9px;
    background: var(--teal);
    border: none;
    color: white;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }

  .vb-btn-confirm:hover { background: var(--teal-mid); }
  .vb-btn-confirm:disabled { opacity: 0.5; cursor: not-allowed; }

  .vb-btn-decline {
    display: flex; align-items: center; gap: 7px;
    padding: 9px 18px;
    border-radius: 9px;
    border: 1px solid var(--red-border);
    background: var(--red-bg);
    color: var(--red);
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .vb-btn-decline:hover { opacity: 0.8; }

  .vb-btn-offer {
    display: inline-flex; align-items: center; gap: 6px;
    background: var(--teal-bg); color: var(--teal);
    border: 1.5px solid var(--teal-border); padding: 9px 18px;
    border-radius: 8px; font-size: 13px; font-weight: 700;
    cursor: pointer; font-family: inherit;
    transition: background 0.15s ease;
  }
  .vb-btn-offer:hover { background: var(--teal-border); }
  .vb-btn-offer:disabled { opacity: 0.5; cursor: not-allowed; }

  .vb-resched-note {
    background: var(--amber-bg); border: 1px solid var(--amber);
    border-radius: 9px; padding: 12px 14px; margin-bottom: 12px;
    font-size: 13px; color: var(--ink); line-height: 1.6;
  }

  .vb-btn-complete {
    display: flex; align-items: center; gap: 7px;
    padding: 9px 18px;
    border-radius: 9px;
    border: 1px solid var(--teal-border);
    background: var(--teal-bg);
    color: var(--teal);
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .vb-btn-complete:hover { opacity: 0.8; }

  /* Empty state */
  .vb-empty {
    text-align: center;
    padding: 60px 24px;
    color: var(--ink-light);
  }
`;

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_BOOKINGS = [
  {
    id: "bk1",
    customer_id: "u1",
    customer_name: "James O.",
    guest_name: null,
    guest_email: null,
    source: "in_app",
    session_title: "Clarity Session",
    duration_minutes: 60,
    price: 120,
    scheduled_at: "2026-03-18T15:00:00Z",
    timezone: "America/New_York",
    status: "requested",
    notes: "I'm working through a major career transition and would love some clarity on my direction.",
    created_at: "2026-03-12T09:22:00Z",
  },
  {
    id: "bk2",
    customer_id: null,
    customer_name: null,
    guest_name: "Sarah Chen",
    guest_email: "sarah@email.com",
    guest_phone: "+1 503 555 0192",
    source: "widget",
    session_title: "Sound Bath (1:1)",
    duration_minutes: 45,
    price: 85,
    scheduled_at: "2026-03-20T18:00:00Z",
    timezone: "America/Los_Angeles",
    status: "confirmed",
    notes: null,
    created_at: "2026-03-11T14:08:00Z",
  },
  {
    id: "bk3",
    customer_id: "u4",
    customer_name: "Priya N.",
    guest_name: null,
    guest_email: null,
    source: "in_app",
    session_title: "Deep Dive Integration",
    duration_minutes: 90,
    price: 175,
    scheduled_at: "2026-03-10T14:00:00Z",
    timezone: "America/New_York",
    status: "completed",
    notes: null,
    created_at: "2026-03-03T11:30:00Z",
  },
  {
    id: "bk4",
    customer_id: "u7",
    customer_name: "Kai T.",
    guest_name: null,
    guest_email: null,
    source: "in_app",
    session_title: "Clarity Session",
    duration_minutes: 60,
    price: 120,
    scheduled_at: "2026-03-05T16:00:00Z",
    timezone: "America/Chicago",
    status: "cancelled",
    notes: "Will need to reschedule.",
    created_at: "2026-02-28T08:45:00Z",
  },
];

const formatDateTime = (iso, tz) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: tz,
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", timeZoneName: "short",
    });
  } catch {
    return iso;
  }
};

// ─── Intake on the card ───────────────────────────────────────────────────────
// Finding a completed form used to mean knowing which form it was and digging through
// Client Forms → Answers. It belongs with the session it was asked for (owner, Jun 2026).
// Kept behind one click: a form can hold things nobody wants on screen while sharing it.

const QUIET_FIELDS = ["heading", "instruction"];

function IntakePanel({ booking }) {
  const asked = booking.intake_asked ?? (booking.intake_forms || []).length;
  const received = booking.intake_received ?? 0;
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!asked) return null;

  const read = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (data) return;
    setLoading(true);
    try {
      setData(await apiFetch(`/intake-forms/for-booking/${booking.id}`));
    } catch {
      setData({ forms: [], notice: "" });
    } finally {
      setLoading(false);
    }
  };

  const allIn = received >= asked;
  return (
    <div style={{
      background: allIn ? "#f2f8f4" : "#fdf8ec",
      border: `1px solid ${allIn ? "#cfe4d6" : "#ecdcb4"}`,
      borderRadius: 10, padding: 12, marginBottom: 12,
    }} data-testid={`booking-intake-${booking.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <ClipboardList size={13} color={allIn ? "#2f6b48" : "#8a6a0a"} />
        <span style={{ fontSize: 12, fontWeight: 700, color: allIn ? "#2f6b48" : "#8a6a0a" }}>
          {allIn
            ? `Intake: ${received} of ${asked} received`
            : `Waiting on ${asked - received} of ${asked}`}
        </span>
        {received > 0 && (
          <button onClick={read} style={{
            fontSize: 12, fontWeight: 600, color: "#2a4a6a", textDecoration: "underline",
          }} data-testid={`read-intake-${booking.id}`}>
            {open ? "Hide answers" : "Read intake"}
          </button>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 10 }}>
          {loading && <span style={{ fontSize: 12, color: "var(--ink-light)" }}>Opening…</span>}
          {data?.forms?.filter((f) => f.response).map((f) => {
            const r = f.response;
            const fields = (r.fields_snapshot || []).filter((x) => !QUIET_FIELDS.includes(x.type));
            return (
              <div key={r.id} style={{
                background: "#fff", border: "1px solid var(--border)", borderRadius: 8,
                padding: 12, marginBottom: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700 }}>{r.form_title}</span>
                  <button onClick={() => window.open(`/print/intake/${r.id}`, "_blank")}
                    style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4,
                             border: "1px solid var(--border)", borderRadius: 999, padding: "3px 10px" }}
                    data-testid={`print-intake-${r.id}`}>
                    <Printer size={11} /> Print
                  </button>
                </div>
                {fields.map((x) => {
                  const given = r.answers?.[x.id];
                  const shown = Array.isArray(given) ? given.join(", ") : given;
                  return (
                    <div key={x.id} style={{ marginBottom: 7 }}>
                      <p style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em",
                                  color: "var(--ink-light)", margin: 0 }}>{x.label}</p>
                      <p style={{ fontSize: 12.5, margin: 0, whiteSpace: "pre-line" }}>
                        {shown === undefined || shown === null || shown === ""
                          ? <em style={{ color: "var(--ink-light)" }}>left blank</em>
                          : String(shown)}
                      </p>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {data?.notice && (
            <p style={{ fontSize: 10.5, color: "var(--ink-light)", margin: "4px 0 0" }}>
              {data.notice}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Booking Card ─────────────────────────────────────────────────────────────

function BookingCard({ booking, onConfirm, onDecline, onComplete, focused, onFocused }) {
  const [expanded, setExpanded]     = useState(booking.status === "requested");
  const [confirmMsg, setConfirmMsg] = useState("");
  const [loading, setLoading]       = useState(false);
  const [localStatus, setLocalStatus] = useState(booking.status);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [previewing, setPreviewing]       = useState(false);
  const [showOffer, setShowOffer]         = useState(false);
  const [localBooking, setLocalBooking]   = useState(booking);
  // Where this one session happens. A practitioner who spins up a new room per session has
  // no standing link to put on the session type, so it belongs here (owner, Jun 2026).
  const [locLink, setLocLink]       = useState(booking.location?.link || "");
  const [locDetail, setLocDetail]   = useState(booking.location?.note || "");
  const [locSaving, setLocSaving]   = useState(false);
  const [locSaved, setLocSaved]     = useState(false);

  const displayName = booking.guest_name || booking.customer_name || "Unknown";
  const isGuest     = booking.source === "widget";
  const locMode     = localBooking.location?.mode || "video";
  const needsLink   = locMode === "video" && !locLink.trim();
  const cardRef     = useRef(null);

  // Picked out on the calendar above: open it and bring it into view.
  useEffect(() => {
    if (!focused) return;
    setExpanded(true);
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => onFocused?.(), 2200);
    return () => clearTimeout(t);
  }, [focused, onFocused]);

  const doConfirm = async () => {
    setLoading(true);
    try {
      // The message box and the link used to be thrown away on submit.
      const updated = await apiFetch(`/practitioner/bookings/${booking.id}/confirm`, {
        method: "PATCH",
        body: JSON.stringify({
          message: confirmMsg || null,
          location_link: locLink || null,
          location_detail: locDetail || null,
        }),
      });
      setLocalBooking(b => ({ ...b, ...updated }));
      setLocalStatus("confirmed");
      onConfirm?.(booking.id, confirmMsg);
    } catch (err) {
      alert(err.message || "Failed to confirm booking");
    } finally {
      setLoading(false);
    }
  };

  const resendProposal = async () => {
    setLoading(true);
    try {
      await apiFetch(`/practitioner/bookings/${booking.id}/resend-proposal`, { method: "POST" });
      alert("Sent again — they have a fresh link and another week to answer.");    } catch (err) {
      alert(err.message || "Could not send that again");
    } finally {
      setLoading(false);
    }
  };

  const saveLocation = async (notify = true) => {
    setLocSaving(true);
    try {
      const updated = await apiFetch(`/practitioner/bookings/${booking.id}/location`, {
        method: "PATCH",
        body: JSON.stringify({
          location_link: locLink || null,
          location_detail: locDetail || null,
          notify_client: notify,
        }),
      });
      setLocalBooking(b => ({ ...b, ...updated }));
      setLocSaved(true);
      setTimeout(() => setLocSaved(false), 2500);
    } catch (err) {
      alert(err.message || "We couldn't save that.");
    } finally {
      setLocSaving(false);
    }
  };

  const submitOffer = async (times, message) => {
    setLoading(true);
    try {
      const res = await apiFetch(`/bookings/${booking.id}/reschedule/offer`, {
        method: "POST", body: JSON.stringify({ times, message: message || null }),
      });
      setLocalBooking(b => ({ ...b, ...res, reschedule_request: null }));
      setShowOffer(false);
      setCancelPreview(null);
    } catch (err) {
      alert(err.message || "We couldn't send those times.");
    } finally {
      setLoading(false);
    }
  };

  const declineRequest = async () => {
    setLoading(true);
    try {
      await apiFetch(`/bookings/${booking.id}/reschedule/request/decline`, {
        method: "POST", body: JSON.stringify({ note: null }),
      });
      setLocalBooking(b => ({ ...b, reschedule_request: null }));
    } catch (err) {
      alert(err.message || "We couldn't decline that request.");
    } finally {
      setLoading(false);
    }
  };

  // Never let a practitioner cancel a paid session without seeing the cost first.
  const askCancel = async () => {
    setPreviewing(true);
    try {
      const preview = await apiFetch(`/bookings/${booking.id}/cancellation-preview`);
      if (!preview.captured) {
        await doDecline();
      } else {
        setCancelPreview(preview);
      }
    } catch {
      setCancelPreview({ captured: true, unavailable: true });
    } finally {
      setPreviewing(false);
    }
  };

  const doDecline = async () => {
    setLoading(true);
    try {
      await apiFetch(`/bookings/${booking.id}/cancel`, { method: "PATCH" });
      setLocalStatus("cancelled");
      setCancelPreview(null);
      onDecline?.(booking.id);
    } catch (err) {
      alert(err.message || "Failed to cancel booking");
    } finally {
      setLoading(false);
    }
  };

  const doComplete = async () => {
    setLoading(true);
    try {
      await apiFetch(`/practitioner/bookings/${booking.id}/complete`, { method: "PATCH" });
      setLocalStatus("completed");
      onComplete?.(booking.id);
    } catch (err) {
      alert(err.message || "Failed to complete booking");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`vb-card${focused ? " vb-card-focused" : ""}`} ref={cardRef}
      data-testid={`booking-card-${booking.id}`}>
      <div className="vb-card-head" onClick={() => setExpanded(v => !v)}>
        <div className={`vb-card-avatar ${isGuest ? "guest" : ""}`}>
          {displayName[0]}
        </div>
        <div className="vb-card-info">
          <p className="vb-card-name">{displayName}</p>
          <div className="vb-card-meta">
            <span className={`vb-source-badge vb-source-${booking.source}`}>
              {booking.source === "widget" ? <><Globe size={9} /> Public</> : <><User size={9} /> In-app</>}
            </span>
            <span className="vb-session-chip">
              <Clock size={11} /> {booking.session_title} · {booking.duration_minutes}min
            </span>
            <span className="vb-card-meta-item">
              <Calendar size={11} /> {formatDateTime(booking.scheduled_at, booking.timezone)}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>${booking.price}</span>
          <span className={`vb-status vb-status-${localStatus}`}>
            {localStatus === "requested"  && <AlertCircle size={10} />}
            {localStatus === "confirmed"  && <Check size={10} />}
            {localStatus === "completed"  && <CheckCircle size={10} />}
            {localStatus === "cancelled"  && <X size={10} />}
            {localStatus}
          </span>
        </div>
        <button className="vb-expand-btn">
          {expanded ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
        </button>
      </div>

      {expanded && (
        <div className="vb-card-detail">
          <div className="vb-detail-grid">
            <div className="vb-detail-field">
              <label>Client</label>
              <p>{displayName}</p>
            </div>
            {booking.guest_email && (
              <div className="vb-detail-field">
                <label>Email</label>
                <p style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Mail size={12} color="var(--ink-light)" /> {booking.guest_email}
                </p>
              </div>
            )}
            {booking.guest_phone && (
              <div className="vb-detail-field">
                <label>Phone</label>
                <p style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Phone size={12} color="var(--ink-light)" /> {booking.guest_phone}
                </p>
              </div>
            )}
            <div className="vb-detail-field">
              <label>Session</label>
              <p>{booking.session_title} · {booking.duration_minutes} min · ${booking.price}</p>
            </div>
            <div className="vb-detail-field">
              <label>Scheduled</label>
              <p>{formatDateTime(booking.scheduled_at, booking.timezone)}</p>
            </div>
            {booking.notes && (
              <div className="vb-detail-field" style={{ gridColumn: "1 / -1" }}>
                <label>Client notes</label>
                <p>{booking.notes}</p>
              </div>
            )}
          </div>

          <IntakePanel booking={booking} />

          {(localStatus === "requested" || localStatus === "confirmed") && (
            <div style={{
              background: "#f4f8fd", border: "1px solid #cfe0f5", borderRadius: 10,
              padding: 12, marginBottom: 12,
            }} data-testid="booking-location-editor">
              <p style={{ fontSize: 12, fontWeight: 700, color: "#2a4a6a", marginBottom: 2 }}>
                Where is this session happening? · {localBooking.location?.label || "Video call"}
              </p>
              <p style={{ fontSize: 11, color: "var(--ink-light)", marginBottom: 8, lineHeight: 1.5 }}>
                {locMode === "video"
                  ? "Paste the meeting link for this session. You can change it any time — your client always sees the current one."
                  : "Anything your client needs to find you for this session."}
              </p>
              {locMode === "video" && (
                <input
                  className="vb-confirm-msg"
                  style={{ minHeight: 0, height: 38, marginBottom: 6 }}
                  placeholder="https://zoom.us/j/..."
                  value={locLink}
                  onChange={e => setLocLink(e.target.value)}
                  data-testid="booking-location-link-input"
                />
              )}
              <input
                className="vb-confirm-msg"
                style={{ minHeight: 0, height: 38 }}
                placeholder={locMode === "in_person"
                  ? "Room number, which door, parking..."
                  : "Optional note for this session"}
                value={locDetail}
                onChange={e => setLocDetail(e.target.value)}
                data-testid="booking-location-detail-input"
              />
              {localStatus === "confirmed" && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                  <button
                    className="vb-btn-confirm"
                    style={{ padding: "7px 14px", fontSize: 12 }}
                    onClick={() => saveLocation(true)}
                    disabled={locSaving}
                    data-testid="booking-location-save-btn"
                  >
                    {locSaving
                      ? <Loader size={12} style={{ animation: "spin 1s linear infinite" }} />
                      : <Check size={12} />}
                    Save & email client
                  </button>
                  {locSaved && (
                    <span style={{ fontSize: 12, color: "var(--teal)" }} data-testid="booking-location-saved">
                      Sent to {displayName}
                    </span>
                  )}
                </div>
              )}
              {localStatus === "requested" && needsLink && (
                <p style={{ fontSize: 11, color: "#8a6a0a", marginTop: 8 }} data-testid="booking-location-warning">
                  No link yet — confirm without one and your client is told you'll send it before the session.
                </p>
              )}
            </div>
          )}

          {/* Confirmation message + actions */}
          {localBooking.reschedule_request && (
            <div className="vb-resched-note" data-testid="reschedule-request-note">
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                This client has asked for a different time
              </p>
              {localBooking.reschedule_request.note && (
                <p style={{ fontStyle: "italic", marginBottom: 8 }}>
                  "{localBooking.reschedule_request.note}"
                </p>
              )}
              <div className="vb-actions">
                <button className="vb-btn-offer" onClick={() => setShowOffer(true)}
                        disabled={loading} data-testid="reschedule-answer-btn">
                  <CalendarClock size={14} /> Offer times
                </button>
                <button className="vb-btn-decline" onClick={declineRequest}
                        disabled={loading} data-testid="reschedule-decline-request-btn">
                  <X size={14} /> Can't move it
                </button>
              </div>
            </div>
          )}

          {localBooking.reschedule_offer && (
            <div className="vb-resched-note" data-testid="reschedule-offer-pending">
              Waiting on your client to pick one of the{" "}
              {(localBooking.reschedule_offer.times || []).length} times you offered.
            </div>
          )}

          {localStatus === "requested" && (
            <>
              <textarea
                className="vb-confirm-msg"
                placeholder="Optional note to your client — it goes in their confirmation email (preparation, what to expect, anything personal)..."
                value={confirmMsg}
                onChange={e => setConfirmMsg(e.target.value)}
                data-testid="booking-confirm-message-input"
              />
              <div className="vb-actions">
                <button className="vb-btn-confirm" onClick={doConfirm} disabled={loading}>
                  {loading ? <Loader size={13} style={{ animation: "spin 1s linear infinite" }} /> : <Check size={14} />}
                  Confirm booking
                </button>
                <button className="vb-btn-offer" onClick={() => setShowOffer(true)}
                        disabled={loading} data-testid="booking-offer-times-btn">
                  <CalendarClock size={14} /> Offer a new time
                </button>
                <button className="vb-btn-decline" onClick={askCancel}
                        disabled={loading || previewing} data-testid="booking-decline-btn">
                  <X size={14} /> Decline
                </button>
                {booking.placed_by_practitioner && (
                  // She proposed this one, so it is waiting on the client rather than on her.
                  <button className="vb-btn-offer" onClick={resendProposal} disabled={loading}
                          data-testid="booking-resend-proposal-btn">
                    <CalendarClock size={14} /> Send the link again
                  </button>
                )}
              </div>
            </>
          )}

          {localStatus === "confirmed" && (
            <div className="vb-actions">
              <button className="vb-btn-complete" onClick={doComplete} disabled={loading}>
                <CheckCircle size={14} /> Mark session complete
              </button>
              <button className="vb-btn-offer" onClick={() => setShowOffer(true)}
                      disabled={loading} data-testid="booking-offer-times-btn">
                <CalendarClock size={14} /> Offer a new time
              </button>
              <button className="vb-btn-decline" onClick={askCancel}
                      disabled={loading || previewing} data-testid="booking-cancel-btn">
                <X size={14} /> Cancel
              </button>
            </div>
          )}

          {localStatus === "completed" && (
            <div style={{ fontSize: 13, color: "var(--ink-light)", display: "flex", alignItems: "center", gap: 6 }}>
              <Star size={13} color="var(--amber)" /> Session completed — client can now leave a review.
            </div>
          )}
        </div>
      )}
      {showOffer && (
        <OfferTimesModal
          clientName={displayName}
          busy={loading}
          onClose={() => setShowOffer(false)}
          onSubmit={submitOffer}
        />
      )}
      {cancelPreview && (
        <CancelWarning
          preview={cancelPreview}
          clientName={displayName}
          busy={loading}
          onKeep={() => setCancelPreview(null)}
          onConfirm={doDecline}
          onOfferTimes={() => { setCancelPreview(null); setShowOffer(true); }}
        />
      )}
    </div>
  );
}


// ─── Offer new times ─────────────────────────────────────────────────────────

/** Up to three alternatives. Moving a session costs neither side anything. */
function OfferTimesModal({ clientName, busy, onClose, onSubmit }) {
  const [slots, setSlots] = useState(["", "", ""]);
  const [message, setMessage] = useState("");

  const filled = slots.filter(Boolean);
  const setSlot = (i, v) => setSlots(s => s.map((x, j) => (j === i ? v : x)));

  const submit = () => {
    if (!filled.length) return;
    onSubmit(filled.map(v => new Date(v).toISOString()), message);
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 90, background: "rgba(20,24,34,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      data-testid="offer-times-modal"
    >
      <div style={{
        background: "#fff", borderRadius: 14, maxWidth: 480, width: "100%",
        padding: 24, boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
      }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>
          Offer {clientName} another time
        </h3>
        <p style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.6, marginBottom: 18 }}>
          Give up to three options and they'll pick whichever suits. Nothing is
          refunded and no fees are charged — the original payment simply moves with
          the session.
        </p>

        {slots.map((value, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <label style={{
              display: "block", fontSize: 12, fontWeight: 600,
              color: "var(--ink-mid)", marginBottom: 4,
            }}>
              Option {i + 1}{i > 0 ? " (optional)" : ""}
            </label>
            <input
              type="datetime-local"
              value={value}
              onChange={e => setSlot(i, e.target.value)}
              style={{
                width: "100%", padding: "9px 12px", borderRadius: 8,
                border: "1.5px solid var(--border)", fontSize: 14,
                fontFamily: "inherit", color: "var(--ink)", background: "#fff",
              }}
              data-testid={`offer-time-${i + 1}`}
            />
          </div>
        ))}

        <label style={{
          display: "block", fontSize: 12, fontWeight: 600,
          color: "var(--ink-mid)", margin: "12px 0 4px",
        }}>
          Message (optional)
        </label>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Sorry, something came up — any of these work?"
          rows={2}
          style={{
            width: "100%", padding: "9px 12px", borderRadius: 8,
            border: "1.5px solid var(--border)", fontSize: 14,
            fontFamily: "inherit", color: "var(--ink)", background: "#fff",
            resize: "vertical",
          }}
          data-testid="offer-message"
        />

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "11px 16px", borderRadius: 9, fontWeight: 700,
              fontSize: 13, border: "1.5px solid var(--border)",
              background: "var(--off-white)", color: "var(--ink)", cursor: "pointer",
            }}
            data-testid="offer-cancel-btn"
          >
            Never mind
          </button>
          <button
            onClick={submit}
            disabled={busy || !filled.length}
            style={{
              flex: 1, padding: "11px 16px", borderRadius: 9, fontWeight: 700,
              fontSize: 13, border: "none", background: "var(--teal)",
              color: "#fff", cursor: "pointer",
              opacity: busy || !filled.length ? 0.5 : 1,
            }}
            data-testid="offer-send-btn"
          >
            {busy ? "Sending…" : `Send ${filled.length || ""} time${filled.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cancel warning ───────────────────────────────────────────────────────────

function CancelWarning({ preview, clientName, busy, onKeep, onConfirm, onOfferTimes }) {
  const money = n => `$${Number(n || 0).toFixed(2)}`;
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 90, background: "rgba(20,24,34,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      data-testid="cancel-warning-modal"
    >
      <div style={{
        background: "#ffffff", borderRadius: 14, maxWidth: 460, width: "100%",
        padding: 24, boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
      }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", marginBottom: 12 }}>
          Cancel this session?
        </h3>

        {preview.unavailable ? (
          <p style={{ fontSize: 14, color: "var(--ink-mid)", lineHeight: 1.6 }}>
            We couldn't work out the refund for this session just now. Cancelling will still
            refund your client in full.
          </p>
        ) : (
          <>
            <p style={{ fontSize: 14, color: "var(--ink-mid)", lineHeight: 1.7 }}
               data-testid="cancel-warning-body">
              This refunds {clientName} <strong style={{ color: "var(--ink)" }}>
              {money(preview.client_refund)}</strong>.
              {preview.practitioner_debit > 0 && (
                <> The <strong style={{ color: "var(--ink)" }}>{money(preview.practitioner_debit)}</strong>{" "}
                card fee is not returned by Stripe, so it will be taken off your next payout.</>
              )}
            </p>
            <div style={{
              marginTop: 16, padding: "12px 14px", borderRadius: 9,
              background: "var(--teal-bg)", border: "1px solid var(--teal-border)",
              fontSize: 13, color: "var(--ink)", lineHeight: 1.6,
            }} data-testid="cancel-reschedule-nudge">
              <strong>Rescheduling costs you nothing</strong> — offer {clientName} another time
              instead and you keep the booking and the fee.
              <button
                onClick={onOfferTimes}
                style={{
                  display: "block", marginTop: 10, background: "var(--teal)",
                  color: "#fff", border: "none", borderRadius: 8,
                  padding: "8px 14px", fontSize: 13, fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit",
                }}
                data-testid="cancel-offer-times-btn"
              >
                Offer a new time instead
              </button>
            </div>
          </>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button
            onClick={onKeep}
            style={{
              flex: 1, padding: "11px 16px", borderRadius: 9, fontWeight: 700, fontSize: 13,
              border: "1.5px solid var(--border)", background: "var(--off-white)",
              color: "var(--ink)", cursor: "pointer",
            }}
            data-testid="cancel-keep-btn"
          >
            Keep the booking
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            style={{
              flex: 1, padding: "11px 16px", borderRadius: 9, fontWeight: 700, fontSize: 13,
              border: "none", background: "var(--red)", color: "#fff", cursor: "pointer",
              opacity: busy ? 0.6 : 1,
            }}
            data-testid="cancel-confirm-btn"
          >
            {busy ? "Cancelling…" : "Cancel and refund"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function VendorBookings() {
  const [bookings, setBookings] = useState([]);
  const [tab, setTab]           = useState("requests");
  const [page, setPage]         = useState(1);
  const [notice, setNotice]     = useState("");
  const [noticeLink, setNoticeLink] = useState("");
  // The month view and the list are two ways of looking at the same sessions, so anything
  // that changes a booking has to reach both (owner, Jun 2026).
  const [refreshKey, setRefreshKey] = useState(0);
  const [focusId, setFocusId]       = useState(null);
  const [placeOpen, setPlaceOpen]   = useState(false);
  const [placeDate, setPlaceDate]   = useState(null);
  const PER_PAGE                = 25;

  useEffect(() => { setPage(1); }, [tab]);

  const fetchBookings = async () => {
    // Fetch every page, not just the first. The list is oldest-first and capped at 100 per
    // request, so an account with more than 100 bookings behind it never saw its newest ones —
    // a session placed three weeks out simply wasn't in the Requests tab (found in testing,
    // Jun 2026). Six pages is 600 sessions, with a stop either way.
    try {
      let page = 1;
      let pages = 1;
      const all = [];
      do {
        const d = await apiFetch(`/practitioner/bookings?per_page=100&page=${page}`);
        const items = d?.bookings || d || [];
        all.push(...items);
        pages = d?.total_pages || 1;
        page += 1;
      } while (page <= pages && page <= 6);
      setBookings(all);
    } catch {
      /* the empty state says it better than an alert would */
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleAction = () => {
    // Refresh list after any action
    fetchBookings();
    setRefreshKey(k => k + 1);
  };

  const requestCount = bookings.filter(b => b.status === "requested").length;
  const upcomingCount = bookings.filter(b => b.status === "confirmed").length;

  const listFor = (t) => bookings.filter(b => {
    if (t === "requests")  return b.status === "requested";
    if (t === "upcoming")  return b.status === "confirmed";
    if (t === "past")      return b.status === "completed";
    if (t === "cancelled") return b.status === "cancelled";
    return true;
  });

  // A session chosen in the month view: switch to the right part of the ribbon, turn to the
  // page it sits on, and let the card open itself.
  const openBooking = (b) => {
    const t = { requested: "requests", confirmed: "upcoming",
                completed: "past", cancelled: "cancelled" }[b.status] || "requests";
    const idx = listFor(t).findIndex(x => x.id === b.id);
    setTab(t);
    if (idx === -1) {
      setNotice("That session is further out than this list reaches — it is on the calendar above.");
      setNoticeLink("");
      return;
    }
    setPage(Math.floor(idx / PER_PAGE) + 1);
    setFocusId(b.id);
  };

  const clearFocus = useCallback(() => setFocusId(null), []);

  const filtered = listFor(tab);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <>
      <style>{styles}</style>
      <div className="vb-root">
        <div className="vb-header">
          <div>
            <h1>Bookings</h1>
            <p>{requestCount} pending · {upcomingCount} upcoming</p>
          </div>
        </div>

        <div className="vb-board">
          <BookingsCalendar
            refreshKey={refreshKey}
            onOpenBooking={openBooking}
            onSchedule={(dateKey) => { setPlaceDate(dateKey); setPlaceOpen(true); }}
          />

          <PlaceSessionPanel
            open={placeOpen}
            onOpenChange={setPlaceOpen}
            prefillDate={placeDate}
            onPlaced={(message, link) => {
              setNotice(message); setNoticeLink(link || ""); handleAction();
            }}
          />
          {notice && (
            <div style={{ fontSize: 13, color: '#1a5a44', background: 'rgba(26,90,68,0.08)',
                          padding: '9px 12px', borderRadius: 8, marginBottom: 14 }}
              data-testid="vendor-bookings-notice">
              {notice}
              {noticeLink && (
                <>
                  <br />
                  <span style={{ fontSize: 12 }}>
                    Their link, if you would rather send it yourself:{' '}
                    <a href={noticeLink} target="_blank" rel="noreferrer"
                       style={{ textDecoration: 'underline' }}
                       data-testid="vendor-bookings-proposal-link">{noticeLink}</a>
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        <div className="vb-tabs">
          {[
            { id: "requests",  label: "Requests",  badge: requestCount },
            { id: "upcoming",  label: "Upcoming",  badge: upcomingCount },
            { id: "past",      label: "Completed", badge: null },
            { id: "cancelled", label: "Cancelled", badge: null },
          ].map(t => (
            <button
              key={t.id}
              className={`vb-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
              data-testid={`vendor-bookings-tab-${t.id}`}
            >
              {t.label}
              {t.badge > 0 && <span className="vb-tab-badge">{t.badge}</span>}
            </button>
          ))}
        </div>

        <div className="vb-list">
          {filtered.length === 0 ? (
            <div className="vb-empty">
              <Calendar size={32} style={{ opacity: 0.3, marginBottom: 12, display: "block", margin: "0 auto 12px" }} />
              <p style={{ fontFamily: "'Literata', serif", fontSize: 18, color: "var(--ink)", margin: "0 0 6px" }}>
                {tab === "requests" ? "No pending requests" : `No ${tab} bookings`}
              </p>
              <p style={{ fontSize: 13, margin: 0 }}>
                {tab === "requests" && "New booking requests will appear here for your review."}
                {tab === "upcoming" && "Confirmed sessions will appear here."}
                {tab === "past"     && "Completed sessions will appear here."}
                {tab === "cancelled" && "Cancelled bookings will appear here."}
              </p>
            </div>
          ) : (
            pageItems.map(b => (
              <BookingCard key={b.id} booking={b} onConfirm={handleAction} onDecline={handleAction}
                onComplete={handleAction} focused={focusId === b.id} onFocused={clearFocus} />
            ))
          )}
        </div>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} testidPrefix="vendor-bookings-pagination" />
      </div>
    </>
  );
}
